import React, { useState, useRef, useEffect } from 'react';
import { Baby, Heart, Crown, Sparkles, Gift, Star, ArrowRight, CheckCircle, Award, Cake } from 'lucide-react';

const ServicesSection = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Baby,
      title: "Produse Nou-Născut",
      description: "Colecție completă de cadouri personalizate pentru bebeluși, de la venirea pe lume până la primele sărbători ale celui mic.",
      features: ["Jucării personalizate", "Body-uri personalizate", "Seturi de nou-născut", "Păturici pufoase"],
      gradient: "from-navy-500 to-navy-700",
      shadowColor: "shadow-navy"
    },
    {
      icon: Crown,
      title: "Trusouri de Botez Premium",  
      description: "Gama de lux cu lucru manual 100%, personalizare completă prin broderie și serigrafie, unde singura limită este imaginația.",
      features: ["Premium pentru fete", "Premium pentru băieți", "Design personalizat", "Lucru manual exclusiv"],
      gradient: "from-gold-500 to-gold-700",
      shadowColor: "shadow-gold"
    },
    {
      icon: Heart,
      title: "Trusouri Standard & Ieftine",
      description: "Trusouri spectaculoase la prețuri accesibile, perfecte pentru cei cu buget limitat sau care au nevoie de livrare rapidă.",
      features: ["Trusouri cu livrare rapidă", "Trusouri standard", "Prețuri accesibile", "Personalizare la cerere"],
      gradient: "from-emerald-500 to-emerald-700",
      shadowColor: "shadow-emerald"
    },
    {
      icon: Sparkles,
      title: "Rochițe & Costume de Botez",
      description: "Creații originale ale designerilor noștri, menite să se asorteze perfect cu trusourile și lumânările de botez.",
      features: ["Rochițe pentru fetițe", "Costume pentru băieți", "Design personalizat", "Asortare perfectă"],
      gradient: "from-navy-600 to-emerald-600",
      shadowColor: "shadow-navy"
    },
    {
      icon: Gift,
      title: "Lumânări de Botez Personalizate",
      description: "Colecție variată de lumânări personalizate cu flori naturale, artificiale, jucării sau accesorii handmade unice.",
      features: ["Flori naturale", "Flori natural-uscate", "Flori artificiale", "Accesorii handmade"],
      gradient: "from-emerald-600 to-gold-600",
      shadowColor: "shadow-emerald"
    },
    {
      icon: Cake,
      title: "Seturi Aniversare #1andepoveste",
      description: "Pentru prima aniversare - tavite de mot, tavite de turtă, ținute aniversare și body-uri, toate realizate 100% manual în atelierul nostru.",
      features: ["Tavite de mot pictate manual", "Tavite de turtă", "Ținute aniversare", "Body-uri personalizate"],
      gradient: "from-gold-600 to-navy-600",
      shadowColor: "shadow-gold"
    }
  ];

  return (
    <section id="servicii" ref={sectionRef} className="py-20 bg-gradient-to-b from-white via-cream-50/30 to-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 -left-20 w-64 h-64 rounded-full bg-gradient-to-r from-navy-100/20 to-gold-100/20 blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 rounded-full bg-gradient-to-r from-emerald-100/20 to-navy-100/20 blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center px-8 py-3 rounded-full glass border border-navy-200/50 text-navy-600 font-medium text-sm mb-6">
            <Crown className="w-4 h-4 mr-2 text-gold-600 animate-pulse" />
            Atelierul Botez de Poveste
          </div>
          
          <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 font-display">
            <span className="gradient-text">Un Botez de Poveste</span><br />
            <span className="text-3xl md:text-4xl lg:text-5xl text-gray-600">o viață de poveste!</span>
          </h3>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Oferim produse pentru o plajă largă de evenimente, începând de la naștere, 
            până la aniversarea de 1 an. Colecții complete destinate exclusiv acestor momente speciale.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <div 
              key={index}
              className={`relative group transition-all duration-500 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 0.1}s` }}
              onMouseEnter={() => setActiveCard(index)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <div className={`modern-card rounded-2xl p-8 h-full relative overflow-hidden transition-all duration-500 ${
                activeCard === index ? `${service.shadowColor} shadow-2xl scale-105` : 'shadow-lg'
              }`}>
                
                {/* Hover Gradient Background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`}></div>
                
                {/* Icon Container */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Floating particles on hover */}
                  {activeCard === index && (
                    <>
                      <div className="absolute -top-2 -right-2 w-2 h-2 bg-gold-400 rounded-full animate-ping"></div>
                      <div className="absolute -bottom-2 -left-2 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                    </>
                  )}
                </div>

                {/* Content */}
                <h4 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-navy-600 transition-colors duration-300">
                  {service.title}
                </h4>
                
                <p className="text-gray-600 leading-relaxed mb-6 group-hover:text-gray-700 transition-colors duration-300">
                  {service.description}
                </p>

                {/* Features List */}
                <div className={`space-y-2 mb-6 transition-all duration-500 ${
                  activeCard === index ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0'
                } overflow-hidden`}>
                  {service.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex} 
                      className="flex items-center text-sm text-gray-600 fade-in-left"
                      style={{ animationDelay: `${featureIndex * 0.1}s` }}
                    >
                      <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <button 
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className={`group/btn w-full flex items-center justify-center px-4 py-3 rounded-xl font-semibold transition-all duration-300 ${
                    activeCard === index 
                      ? `bg-gradient-to-r ${service.gradient} text-white shadow-lg hover:shadow-xl` 
                      : 'bg-gray-100 text-gray-700 hover:bg-cream-50 hover:text-navy-600'
                  }`}
                >
                  Vezi Colecția
                  <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                </button>

                {/* Card Border Glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-20 blur transition-opacity duration-500 -z-10`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Section */}
        <div className={`text-center transition-all duration-1000 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: '0.8s' }}>
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-navy-500/10 via-gold-500/10 to-emerald-500/10 rounded-3xl blur-xl"></div>
            <div className="relative glass-cream rounded-3xl p-12 border border-gold-200/30 hover-lift shadow-lg">
              <div className="flex justify-center mb-6">
                <div className="flex -space-x-4">
                  {[Crown, Award, Star].map((Icon, index) => (
                    <div 
                      key={index} 
                      className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg float border-4 border-white ${
                        index === 0 ? 'bg-gradient-to-br from-navy-600 to-navy-700' :
                        index === 1 ? 'bg-gradient-to-br from-gold-600 to-gold-700' :
                        'bg-gradient-to-br from-emerald-600 to-emerald-700'
                      }`}
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  ))}
                </div>
              </div>
              
              <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-display">
                Căutați ceva <span className="gradient-text">personalizat complet</span>?
              </h4>
              
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Putem produce trusouri în orice model, tematică sau coloristică. 
                Pentru că botezul este evenimentul care ne recomandă - avem cea mai impresionantă colecție!
              </p>
              
              {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="morph-button px-8 py-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white rounded-full hover:from-navy-700 hover:to-navy-800 font-semibold shadow-navy hover:shadow-2xl transition-all duration-300"
                >
                  <span className="flex items-center justify-center">
                    <Crown className="w-5 h-5 mr-2" />
                    Personalizare Completă
                  </span>
                </button>
                
                <button 
                  onClick={() => window.open('tel:+40740048888')}
                  className="morph-button px-8 py-4 border-2 border-gold-600 text-gold-700 rounded-full hover:bg-gold-600 hover:text-white font-semibold transition-all duration-300"
                >
                  <span className="flex items-center justify-center">
                    <Award className="w-5 h-5 mr-2" />
                    Livrare Rapidă
                  </span>
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-32 left-10 w-3 h-3 bg-navy-300 rounded-full opacity-40 particle-1"></div>
      <div className="absolute top-64 right-16 w-2 h-2 bg-gold-400 rounded-full opacity-30 particle-2"></div>
      <div className="absolute bottom-32 left-20 w-4 h-4 bg-emerald-300 rounded-full opacity-50 particle-1"></div>
    </section>
  );
};

export default ServicesSection;